<template lang="pug">
	.flex-col.creative--creative-approve.full-height.full-width(:style="{ '--toolbarHeight': `${this.toolbarHeight}px` }", @mousemove="onMouseMove", @mousedown="onMouseDown")
		template(v-if="isLoading && !approvalInitialized")
			.flex-col.full-height.full-width.center.align-center
				Loader.font-big(:text="$tCaption('Loading, please wait', '@actions') + (loadingPercentage ? ` ${loadingPercentage}%` : '') + '...'")
		template(v-if="approvalInitialized")
			.flex-row.full-height.full-width
				.flex-1(@mousewheel="onMouseWheel")
					.flex-col.full-height.full-width
						.flex-1.canvas-holder(ref="canvasHolder")
							DxScrollView.canvas-area(ref="canvasArea", direction="horizontal", showScrollbar="always")
								.content
									.flex-col(v-if="buffer", @click="onCanvasAreaClick", :style="{ '--canvas-width': `${canvasWidth}px`, '--canvas-height': `${canvasHeight}px` }")
										.ruler-area(:style="{ '--width': `${rulerWidth}px` }")
											AudioTrackerRuler(v-if="buffer", ref="ruler", :totalTime="buffer.duration", :pixelsPerSecond="pixelsPerSecond", :width="rulerWidth", :step="16", fontSize="12px")
												.ruler-comment-item(v-for="comment in rootComments.filter(f => f.time <= buffer.duration)", :style="{ '--position': `${comment.time * pixelsPerSecond}px` }", :class="[comment.status > 0 || comment.commentStatus > 0 ? 'resolved' : 'issue']")
										canvas.canvas(ref="canvas")
										.current-time(ref="currentTimeHanlder")
										.active-comment(v-if="activeComment", :style="{ '--position': `${activeComment.time * pixelsPerSecond}px` }", :class="[activeComment.status > 0 || activeComment.commentStatus > 0 ? 'resolved' : 'issue']")
											.message {{activeComment.message}}
							.canvas-area-controls
								.flex-row.space
									.button.flex-row.align-center.center(:class="{ disabled: isLoading }", @click="() => zoom(2)")
										fas(icon="plus")
									.button.flex-row.align-center.center(:class="{ disabled: isLoading }", @click="() => zoom(0.5)")
										fas(icon="minus")
						.controls-area.flex-col.center.align-center
							.flex-row.full-width
								.total-line(ref="totalLine", @click="onTotalLineClick")
									.comment-item(v-for="comment in rootComments", :style="{ '--position': `${comment.timePercentage}%` }", :class="[comment.status > 0 || comment.commentStatus > 0 ? 'resolved' : 'issue']")
							.flex-row.flex-1.justify.align-center.lr-padding
								.left.flex-1.flex-col.space.align-start.full-height.approvers-list
									.flex-col
										.font-small.upper.grey {{$tCaption('Approvers')}}
										.flex-row.wrap(v-if="approvers.length")
											.flex-row(v-for="approver in approvers")
												.approver.flex-row.align-center.space(:class="[approver.Status === 0 ? 'waiting' : (approver.Status === 1 ? 'approved' : 'declined')]", 
													:title="approver.Status === 0 ? $tCaption('Waiting for approve') : (approver.Status === 1 ? $tCaption('Approved') : $tCaption('declined'))")
													fas(size="sm", :icon="approver.Status === 0 ? 'hourglass-half' : (approver.Status === 1 ? 'check' : 'xmark')")
													.text {{approver.Name}}
									.flex-col(v-if="approval")
										.font-small.upper.grey {{$tCaption('Approval info')}}
										.flex-row.space
											.grey {{$tCaption('Author')}}:
											span {{author}}
										.flex-row.space
											.grey {{$tCaption('Created')}}:
											span {{$moment(approval.Created).format('L')}}
										.flex-row.space(v-if="approval.Description")
											.grey {{$tCaption('Description')}}:
											span(:title="approval.Description") {{$Data.limitText(approval.Description, 40)}}
								.flex-row.play-controls.space-medium.align-center.center.flex-1
									.button.flex-row.align-center.center(:class="{ disabled: isLoading }", @click="() => jumpTo('start')")
										fas(icon="backward")
									.button.lg.flex-row.align-center.center(:class="{ disabled: isLoading }", @click="() => togglePlay()")
										fas(:icon="isPlaying ? 'pause' : 'play'", size="lg")
									.button.flex-row.align-center.center(:class="{ disabled: isLoading }", @click="() => jumpTo('end')")
										fas(icon="forward")
								.right.flex-1
									.flex-row.space-large.justify-end(v-if="currentApprover")
										.flex-row.space
											DxButton(:text="currentApprover.Status === 1 ? $tCaption('Approved') : $tCaption('Approve')", :type="currentApprover.Status === 0 || currentApprover.Status === 1 ? 'success' : 'normal'", icon="fas fa-check", @click="updateStatus(true)", :disabled="isChangingStatus")
											DxButton(:text="currentApprover.Status === 2 ? $tCaption('Declined') : $tCaption('Decline')", :type="currentApprover.Status === 0 || currentApprover.Status === 2 ? 'danger' : 'normal'", icon="fas fa-times", @click="updateStatus(false)", :disabled="isChangingStatus")
										DxButton(:text="$tCaption('Comment')", type="normal", icon="fas fa-comment", @click="showCommentPopup()")
				.comments-area
					.flex-col.full-height
						.flex-row.justify.space.align-center.comments-panel
							.flex-row.space.align-center.font-small
								.upper.grey {{$tCaption('Comments')}}
								.comment-button(v-if="currentApprover", @click="showCommentPopup()")
									i.fas.fa-plus
							.grey
								Loader(v-if="isCommentsLoading", text="")
								.comment-button(v-else, @click="() => { isCommentsLoading++; loadApprovers().then(() => loadComments()).finally(() => isCommentsLoading--); }")
									i.fas.fa-refresh
						.flex-row.space.comments-panel
							.comment-badge.font-small.flex-row.space.align-center(@click="filterActive = !filterActive", :class="{ active: filterActive }")
								i.fas.fa-filter
								span {{$tCaption('Active')}}
							.comment-badge.font-small.flex-row.space.align-center(@click="filterResolved = !filterResolved", :class="{ active: filterResolved }")
								i.fas.fa-filter
								span {{$tCaption('Resolved')}}
						DxScrollView.flex-1(ref="commentsArea", direction="vertical", showScrollbar="always")
							.flex-col.space-medium
								.comment(v-for="comment in comments", :id="`comment_${comment.id}`", :class="{ answer: comment.answerTo, active: activeComment && activeComment.time === comment.time, own: comment.isAuthor }", 
									@click="e => onCommentClick(comment)"
								)
									.flex-col.space.flex-1
										.flex-row.justify
											.left.flex-row.align-center.space(v-if="!comment.answerTo")
												.upper.time.greyed(v-if="comment.time && !comment.answerTo") {{formatTime(comment.time)}}
												.time.font-small.status-text(:class="[comment.status > 0 || comment.commentStatus > 0 ? 'resolved' : 'issue']") {{comment.status > 0 || comment.commentStatus > 0 ? $tCaption('Resolved') : $tCaption('Waiting fix')}}
											.right.flex-row.space.align-center
												.font-mini.upper.date.greyed {{$moment(comment.date).format('L')}}
												.comment-button(v-if="comment.canBeRemoved && comment.approver === currentApprover ", @click="removeComment(comment)")
													i.fas.fa-times
										.flex-row.space(v-if="comment.level > 1 && comment.answerToComment")
											i.font-small.max-width-200.text-overflow.overflow.nowrap(:title="comment.answerToComment.message") "{{comment.answerToComment.message}}"
										.flex-row.align-center.space
											.status(:class="[comment.status > 0 || comment.commentStatus > 0 ? 'resolved' : 'issue', comment.commentStatus === 3 ? 'declined' : '', comment.commentStatus === 2 ? 'fixed' : '']")
											.message {{comment.message}}
										.flex-row.justify.space.align-center
											.font-tiny.author.greyed {{comment.isAuthor ? $tCaption('You') : comment.author}}
											.comment-button(@click="showCommentPopup(comment.id)", :title="$tCaption('Reply')")
												i.fas.fa-share
										.flex-row.space(v-if="comment.isAuthor && comment.status === 0 && comment.commentStatus === 0 && !comment.answerTo")
											.comment-button.flex-row.space-small.align-center.green.bordered(@click="markComment(comment, 2)")
												fas(icon="check")
												span {{$tCaption('Mark resolved')}}
											.comment-button.flex-row.space-small.align-center.red.bordered(@click="markComment(comment, 3)")
												fas(icon="times")
												span {{$tCaption('Mark declined')}}
		Popup(v-if="commentPopup.visible", :popupVisible="commentPopup.visible" :width="580", :height="245", :title="commentPopup.answerTo ? $tCaption('Reply') : `${$tCaption('Add comment at')} ${formatTime($currentTime)}`", 
			:closeOnOutsideClick="true" @hiding="commentPopup.visible = false"
		)
			DxTextArea(:value.sync="commentPopup.text", valueChangeEvent="input", stylingMode="filled")
			template(slot="actions-right")
				DxButton(:text="$tCaption('OK', '@actions')", type="default", @click="addComment()", :disabled="isLoading || !commentPopup.text")
				DxButton(:text="$tCaption('Cancel', '@actions')", @click="commentPopup.visible = false")
</template>

<script>
	import _ from 'lodash';
	import Loader from 'app-core/Loader.vue';
	import DxScrollView from 'devextreme-vue/scroll-view';
	import AudioTrackerRuler from 'view/audioTracker/AudioTrackerRuler.vue';
	import DxButton from 'devextreme-vue/button';
	import Popup from 'app-core/Popup.vue';
	import DxTextArea from 'devextreme-vue/text-area';
	import axios from 'axios';

	export default {
		translationCategory: 'creative.Approve',
		name: 'creative-approve',
		components: {
			Loader,
			DxScrollView,
			AudioTrackerRuler,
			DxButton,
			Popup,
			DxTextArea
		},
		props: [
			'creativeApprovalId'
		],

		data() {
			return {
				isCreativeLoading: false,
				isLoading: 0,
				buffer: null,
				audioContext: null,
				pixelsPerSecond: 100,
				canvasWidth: 2300,
				canvasHeight: 300,
				toolbarHeight: 0,
				isPlaying: false,
				bufferSource: null,
				audioContextStartTime: 0,
				mouseMoved: 0,
				commentPopup: {
					visible: false,
					text: null,
					answerTo: null
				},
				activeComment: null,
				approval: null,
				approvalComments: [],
				approvers: [],
				isCommentsLoading: false,
				approvalInitialized: false,
				isChangingStatus: false,
				filterActive: false,
				filterResolved: false,
				loadingPercentage: null
			};
		},
		computed: {
			rulerWidth() {
				return Math.max(this.canvasWidth, this.$refs?.canvasArea?.$el?.offsetWidth || 0);
			},
			comments() {
				let statuses = [];
				if (this.filterActive)
					statuses.push(...[0,2]);
				if (this.filterResolved)
					statuses.push(1);

				const allResults = [];
				const getItem = (f, level) => {
					const item = {
						...f,
						level,
						timePercentage: f.time * 100 / this.buffer?.duration,
						isAuthor: f.approver && ((f.approver === this.currentApprover) || (f.userId === this.$auth.userId()))
					};
					allResults.push(item);
					item.subItems = this.commentsData.filter(s => f.id === s.answerTo).map(f => getItem(f, level + 1));
					item.canBeRemoved = !item.subItems.length;
					if (f.answerTo) {
						item.answerToComment = this.commentsData.find(d => d.id === f.answerTo)
					}
					return item;
				}
				let comments = this.commentsData.filter(f => !f.answerTo && (!statuses.length || statuses.indexOf(f.status) > -1));
				_.orderBy(comments, f => f.time).map(f => getItem(f, 0));
				
				return allResults;
			},
			rootComments() {
				return this.comments.filter(f => !f.answerTo);
			},
			allowKeyHandling() {
				return !this.commentPopup.visible;
			},
			isOwner() {
				return this.approval?.CreativeOwnerId === this.$auth.companyId();
			},
			currentApprover() {
				return this.approvers.find(f => `${f.Email}`.toLowerCase() === `${this.$auth.email()}`.toLowerCase());
			},
			commentsData() {
				return this.approvalComments.map(f => {
					let approver = this.approvers.find(a => a.Oid === f.ApproverId);
					let comment = {
						id: f.Oid,
						time: f.Time / 1000,
						approver,
						author: f.ApproverName || f.UserName,
						message: f.Message,
						date: new Date(f.Created),
						status: approver?.Status,
						commentStatus: f.Status || 0,
						answerTo: f.AnswerTo,
						userId: f.UserId,
						sourceComment: f
					}
					return comment;
				});
			},
			author() {
				return this.approval?.CreatedBy?.Contact?.Title || this.approval?.CreatedBy?.Name || '';
			}
		},
		watch: {
			pixelsPerSecond() {
				this.canvasWidth = this.buffer?.duration * this.pixelsPerSecond;
				this.refreshScrolls();
			}
		},
		mounted() {
			if (__DEBUG__)
				window.approve = this;

			this.loadApproval();

			this.$drawAudioData = _.debounce(this.drawAudioData, 500, {
				'leading': false,
				'trailing': true,
				'maxWait': 1000
			});

			this.toolbarHeight = document.body.getElementsByClassName('header-toolbar')[0]?.offsetHeight || 0;

			document.body.addEventListener('keydown', this.onKeyDown);
		},
		destroyed() {
			document.body.removeEventListener('keydown', this.onKeyDown);
		},
		methods: {
			checkQueryActiveComment() {
				if (this.$route?.query?.activeComment) {
					const comment = this.comments.find(f => f.id === this.$route?.query?.activeComment);
					if (comment)
						this.onCommentClick(comment);
				}
			},
			markComment(comment, status) {
				this.$confirmMessage({
					title: this.$tCaption('Confirm action', '@actions'),
					text: this.$tCaption(`Are you sure you want to mark the comment COMMENT_STATUS?`).replace(/COMMENT_STATUS/g, status === 2 ? this.$tCaption('resolved') : this.$tCaption('declined')),
					handler: () => {
						comment.sourceComment.Status = status;
						comment.status = status;
						this.$Data.postExternalApi('CreativeApproval/CreateUpdateComment', {
							...comment.sourceComment,
							ApprovalId: this.approval?.Oid,
							Status: status
						});
					}
				});
			},
			loadApproval() {
				this.approvalInitialized = false;
				this.isLoading++;
				this.$Data.getExternalApi('CreativeApproval/GetApproval', `creativeApprovalId=${this.creativeApprovalId}`)
					.then(r => {
						this.approval = r;
						if (this.approval) {
							this.loadApprovers();
							this.loadComments();
						} else {
							this.$notifyError(this.$tCaption('There is no such approval registered in system.'))
						}
					})
					.finally(() => {
						if (this._isDestroyed) return;
						this.loadAudio().finally(() => {
							if (this._isDestroyed) return;
							this.isLoading--;
							this.approvalInitialized = true;
							setImmediate(() => {
								this.pixelsPerSecond = this.$refs.canvasArea.$el.offsetWidth / Math.min(20, this.buffer?.duration);
								this.drawAudioData();
							});
						});
					});
			},
			loadApprovers() {
				this.isLoading++;
				return this.$Data.getExternalApi('CreativeApproval/GetApprovers', `creativeApprovalId=${this.approval?.Oid}`).then(r => {
					this.approvers = r?.Results || [];
				}).finally(() => this.isLoading--);
			},
			loadComments() {
				this.isLoading++;
				this.isCommentsLoading++;
				this.$Data.getExternalApi('CreativeApproval/GetComments', `approvalId=${this.approval?.Oid}`).then(r => {
					this.approvalComments = r?.Results || [];
					this.checkQueryActiveComment();
				}).finally(() => {
					this.isLoading--;
					this.isCommentsLoading--;
					this.calcActiveComment();
				});
			},
			async updateStatus(accepted) {
				if (!this.currentApprover) return;
				this.isChangingStatus = true;
				const status = accepted ? 1 : 2;
				this.$Data
					.getExternalApi('CreativeApproval/UpdateStatus', `approverId=${this.currentApprover?.Oid}&status=${status}`)
					.then(() => {
						this.isChangingStatus = false;
						this.currentApprover.Status = status;
					});
			},
			onKeyDown(e) {
				if (!this.allowKeyHandling) return;

				if (e.keyCode === 32) {
					this.togglePlay();
				}

				if (e.keyCode === 8) {
					this.jumpTo('start');
				}
			},
			calcActiveComment() {
				this.activeComment = _.last(this.rootComments.filter(f => f.status !== 1 && f.time <= this.$currentTime));
				if (!this.activeComment)
					this.activeComment = _.last(this.rootComments.filter(f => f.time <= this.$currentTime));
			},
			showCommentPopup(answerTo) {
				this.commentPopup.visible = false;
				this.commentPopup.text = null;
				this.commentPopup.answerTo = answerTo;
				setImmediate(() => {
					if (this._isDestroyed) return;
					this.commentPopup.visible = true;
				});
			},
			async addComment() {
				this.commentPopup.visible = false;
				let comment = (await this.$Data.postExternalApi('CreativeApproval/CreateUpdateComment', {
					ApprovalId: this.approval?.Oid,
					ApproverId: this.currentApprover?.Oid,
					Time: parseInt(this.$currentTime * 1000),
					Message: this.commentPopup.text,
					AnswerTo: this.commentPopup.answerTo,
				}))?.Data;
				if (comment)
					this.approvalComments.push(comment);
				this.setCurrentTime(this.$currentTime);
			},
			onCommentClick(comment) {
				let cmt = comment.answerTo ? this.comments.find(f => f.id === comment.answerTo) : comment;
				if (cmt?.time !== void 0) {
					this.refreshIfPlaying(() => {
						this.setCurrentTime(cmt.time);
					});
				}
			},
			removeComment(comment) {
				this.$confirmMessage({
					title: this.$tCaption('Confirm action', '@actions'),
					text: this.$tCaption('Are you sure you want to delete the current comment?'),
					handler: () => {
						this.$Data.getExternalApi('CreativeApproval/DeleteComment', `id=${comment.id}`).then(() => {
							this.$notifySuccess(this.$tCaption('Comment has been removed'));
							this.approvalComments.splice(this.approvalComments.findIndex(f => f.Oid === comment.id), 1);
						});
					}
				});
			},
			onTotalLineClick(e) {
				let percent = Math.min(100, e.offsetX / this.$refs.totalLine.offsetWidth || 0);
				if (__DEBUG__)
					console.debug('onTotalLineClick', { e, time: percent * this.buffer.duration });
				this.refreshIfPlaying(() => {
					this.setCurrentTime(percent * this.buffer.duration, { area: 'player' });
				});
			},
			formatTime(secs) {
				secs = secs || 0;
				let format = secs > 3600 ? 'HH:mm:ss' : 'mm:ss';
				return this.$moment.utc(secs*1000).format(format);
			},
			zoom(coef) {
				this.pixelsPerSecond *= coef;
			},
			jumpTo(v) {
				this.refreshIfPlaying(() => {
					switch(v) {
						case 'start':
							this.setCurrentTime(0, { area: 'player' });
							break;
						case 'end':
							this.setCurrentTime(this.buffer.duration, { area: 'player' });
							break;
					}
				});
				
			},
			runAudioTimer(v) {
				if (this.$audioTimer)
					clearTimeout(this.$audioTimer);
				if (v) {
					this.$audioTimer = setTimeout(() => {
						if (this.isPlaying) {
							this.setCurrentTime(this.audioContext.currentTime - this.audioContextStartTime, { area: 'player' });
							this.runAudioTimer(true);
						}
					}, 0.1);
				}
			},
			togglePlay(forcedState, preventTimeCheck) {
				if (this.isPlaying || forcedState === false) {
					this.bufferSource.stop();
					this.bufferSource = null;
					this.isPlaying = null;
				} else if (!this.isPlaying || forcedState === true) {
					if (!preventTimeCheck && this.$currentTime >= this.buffer.duration) {
						this.$currentTime = 0;
					}
					this.bufferSource = this.audioContext.createBufferSource();
					this.bufferSource.loop = false;
					this.bufferSource.buffer = this.buffer;
					this.bufferSource.connect(this.audioContext.destination);
					this.bufferSource.start(this.audioContext.currentTime, this.$currentTime);
					this.audioContextStartTime = (this.audioContext.currentTime || 0) - (this.$currentTime || 0);
					this.isPlaying = true;
					this.runAudioTimer(true);
				}
			},
			setCurrentTime(v, opts) {
				this.$currentTime = v || 0;
				this.$refs.currentTimeHanlder.style.setProperty('--position', `${v * this.pixelsPerSecond}px`);
				
				if (this.$currentTime > this.buffer.duration) {
					this.togglePlay(false, true);
				}

				setTimeout(() => {
					if (this._isDestroyed) return;
					this.calcScrollPositionFromTimeline();
					if (opts?.area === 'player') {
						this.calcCommentsScrollFromTimeline();
					}
					this.refreshScrolls();
				}, 300);
				
				this.calcActiveComment();
			},
			refreshIfPlaying(callback) {
				var isPlaying = this.isPlaying;
				if (isPlaying)
					this.togglePlay(false, true);
				if (callback)
					callback();
				if (isPlaying)
					this.togglePlay(true, true);
			},
			onCanvasAreaClick(e) {
				if (__DEBUG__)
					console.debug('onCanvasAreaClick', { e });
				if (Math.abs(this.mouseMoved) < 10) {
					this.refreshIfPlaying(() => {
						this.setCurrentTime(e.offsetX / this.pixelsPerSecond, { area: 'player' });
					});
				}
			},
			async loadAudio() {
				const audioUrl = this.approval?.AudioFileToApprove?.AzureFileUrl;

				if (__DEBUG__)
					console.debug('Load audio', { audioUrl });

				if (!audioUrl) return Promise.resolve(null);

				if (!this.audioContext) {
					this.audioContext = new AudioContext({
						sampleRate: 48000,
					});
				}

				const blob = (await axios.get(audioUrl, {
					onDownloadProgress: (evt) => {
						this.loadingPercentage = Math.floor(evt.loaded / evt.total * 100);
					},
					responseType: 'blob',
				}))?.data;

				this.buffer = await this.$Data.getAudioBufferFromBlob(blob, this.audioContext);
			},
			drawAudioData() {
				let buffer = this.buffer;
				if (!buffer) return;
				if (__DEBUG__)
					console.debug('drawAudioData', { buffer });
				let leftChannel = buffer.getChannelData(0);
				let canvas = this.$refs.canvas;
				let context = this.$refs.canvas.getContext('2d');

				let canvasArea = this.$refs.canvasArea.$el;
				let canvasHolder = this.$refs.canvasHolder;
				let secondsPerScreen = canvasArea.offsetWidth / this.pixelsPerSecond;
				let drawLines = buffer.duration * 1000;

				this.canvasWidth = buffer.duration * this.pixelsPerSecond;
				this.canvasHeight = canvasHolder.offsetHeight;

				let realCanvasWidth = buffer.duration * this.pixelsPerSecond;

				canvas.width = realCanvasWidth;
				canvas.height = this.canvasHeight;

				let canvasWidth = this.canvasWidth;
				let canvasHeight = this.canvasHeight;

				context.save();
				context.fillStyle = 'transparent';
				context.fillRect(0, 0, realCanvasWidth, canvasHeight);
				context.strokeStyle = '#f27649';
				context.translate(0, canvasHeight / 2);
				context.lineWidth = Math.max(1, Math.min(5, 1 * 15/secondsPerScreen));

				let totallength = leftChannel.length;
				let eachBlock = Math.floor(totallength / drawLines);
				let lineGap = (realCanvasWidth/drawLines);

				if (__DEBUG__)
					console.debug('Draw image', { canvasWidth, realCanvasWidth, lineGap, eachBlock, totallength, drawLines, secondsPerScreen, canvasHeight });

				context.beginPath();
				context.lineCap = 'round';
				for(let i = 0; i <= drawLines; i++){
					let audioBuffKey = Math.floor(eachBlock * i);
					let x = i * lineGap;
					let y = leftChannel[audioBuffKey] * canvasHeight / 2;
					context.moveTo(x, y);
					context.lineTo(x, y * -1);
				}
				context.stroke();
				context.restore();

				this.refreshScrolls();
			},
			refreshScrolls() {
				setImmediate(() => {
					if (this._isDestroyed) return;
					this.$refs.canvasArea?.instance?.release();
				});
			},
			calcScrollPositionFromTimeline() {
				if (this._isDestroyed) return;
				const delta = 50;
				const start = this.$refs.canvasArea?.instance?.scrollLeft();
				const end = this.$refs.canvasArea?.instance?.scrollLeft() + this.$refs.canvasArea?.$el.offsetWidth - delta;
				const timelinePosition = this.$refs.currentTimeHanlder.offsetLeft || 0;

				if (timelinePosition < start) {
					this.$refs.canvasArea?.instance.scrollTo({
						left: Math.max(0, start - this.$refs.canvasArea?.$el.offsetWidth)
					});
				} else if (timelinePosition > end) {
					this.$refs.canvasArea?.instance.scrollTo({
						left: start + this.$refs.canvasArea?.$el.offsetWidth - delta
					});
				}
			},
			calcCommentsScrollFromTimeline() {
				if (this.activeComment && this.$lastActiveComment !== this.activeComment) {
					this.$refs.commentsArea?.instance?.scrollToElementTopLeft(`#comment_${this.activeComment.id}`);
				}
				this.$lastActiveComment = this.activeComment;
			},
			onMouseMove(e) {
				if (e.buttons === 1) {
					const scroll = this.$refs.canvasArea?.instance;
					if (scroll) {
						scroll.scrollTo({
							left: scroll.scrollLeft() - e.movementX,
							top: scroll.scrollTop() - e.movementY
						});
						this.mouseMoved += e.movementX;
					}
				}
			},
			onMouseWheel(e) {
				if (e.ctrlKey) {
					this.zoom(e.deltaY > 0 ? 0.5 : 2);
					e.stopPropagation();
					e.preventDefault();
				} else {
					this.$refs.canvasArea?.instance?.scrollBy({ left: e.deltaY });
				}
			},
			onMouseDown(e) {
				this.mouseMoved = 0;
			}
		},
	};
</script>

<style lang='scss'>
	@import "main/themes/generated/variables.base.scss";

	.creative--creative-approve {
		width: 100vw;
		height: calc(100vh - var(--toolbarHeight));
		top: calc(var(--toolbarHeight));
		position: fixed;

		.canvas-holder {
			position: relative;

			.canvas-area {
				background: #2a393e;
				background: linear-gradient(180deg, #3a4042 0%, #121e22 50%, #363d40 100%);
				width: calc(100vw - 250px);
			}

			.canvas-area-controls {
				position: absolute;
				bottom: 10px;
				right: 10px;
			}
		}

		.controls-area {
			background: #444d50;
			border-top: 1px solid #5e696c;
			height: 200px;
		}

		.comments-area {
			width: 250px;
			background: #444d50;
			border-left: 1px solid #5e696c;
			position: relative;

			.comment {
				color: #fff;
				padding: 10px;
				margin: 1px 2px 0px 6px;
				background: rgba(255,255,255,0.1);
				cursor: pointer;
				transition: 0.2s;
				border-radius: 4px;

				&:first-child {
					margin-top: 6px;
				}

				&.own {
					.author {
						color: orange;
					}
				}

				.greyed {
					color: rgba(255,255,255,0.4);
				}

				.status {
					min-width: 14px;
					min-height: 14px;
					background: #ffc511;
					border-radius: 30px;

					&.resolved, &.fixed {
						background: rgb(121, 134, 136);
					}

					&.declined {
						background: #c63535;
					}
				}

				.status-text {
					color: #ffc511;

					&.resolved, &.fixed {
						color: rgb(121, 134, 136);
					}

					&.declined {
						color: #c63535;
					}
				}

				&.answer {
					margin-left: 20px;
					background: #ffffff0a;

					.status {
						display: none;
					}
				}

				&:hover {
					background: rgba(255,255,255,0.2);
				}

				&.active {
					border-left: 2px solid #c0c0c0;
				}
			}
		}

		.canvas {
			position: absolute;
			left: 0px;
			top: 0px;
			height: var(--canvas-height);
			width: var(--canvas-width);
		}

		.ruler-area {
			height: 30px;
			width: var(--width);
			overflow: hidden;

			.audio-tracker-ruler {
				height: 30px !important;

				.rule-holder {
					height: 30px !important;
				}

				.tick {
					border-left: 1px solid rgba(255,255,255,0.1) !important;

					.number-value {
						color: rgba(255,255,255,0.3) !important;
						margin-top: 5px !important;
						margin-left: 4px !important;
					}
				}
			}
		}

		.current-time {
			position: absolute;
			height: 100%;
			border-left: 4px solid #ff5d5d17;
			border-right: 2px solid #ff5d5d;
			left: calc(var(--position));
			margin: -4px;
		}

		.button {
			padding: 10px;
			background: rgb(192, 192, 192);
			border-radius: 40px;
			color: #333;
			cursor: pointer;
			transition: 0.2s;
			width: 40px;
			height: 40px;
			border: 5px solid #333a3d;

			&.lg {
				width: 60px;
				height: 60px;
			}

			&:hover {
				background: $base-accent;
			}

			&.disabled {
				opacity: 0.4;
				pointer-events: none;
			}
		}

		.total-line {
			width: 100%;
			height: 10px;
			background: #333a3d;
			margin: 20px;
			border-radius: 8px;
			border: 1px solid rgba(255,255,255,0.15);
			position: relative;
			overflow: hidden;
			cursor: pointer;

			.comment-item {
				position: absolute;
				left: calc(var(--position));
				height: 100%;
				width: 10px;
				background: #ffc511;
				pointer-events: none;

				&.resolved {
					background: rgb(121 134 136);
				}
			}
		}

		.ruler-comment-item {
			position: absolute;
			left: calc(var(--position));
			height: 30px;
			width: 1px;
			background: #ffc511;
			pointer-events: none;

			&.resolved {
				background: rgb(121 134 136);
			}
		}

		.comment-button {
			color: rgba(255,255,255,0.3);
			cursor: pointer;
			transition: 0.3s;
			padding: 0px;
			user-select: none;

			&:hover {
				color: rgba(255,255,255,0.8);
			}

			&.bordered {
				padding: 2px 6px;
				border-radius: 2px;
				font-size: 10px;
			}

			&.green {
				background: #6eb56e;
				color: #fff;

				&:hover {
					background: #5d8f5d;
				}
			}

			&.red {
				background: #cb5050;
				color: #fff;

				&:hover {
					background: #a54141;
				}
			}
		}

		.active-comment {
			position: absolute;
			bottom: 20%;
			height: 30%;
			left: calc(var(--position));
			transition: 0.2s;
			border-left: 1px solid #ffc511;

			> .message {
				padding: 6px;
				background: #ffc511;
				position: absolute;
				white-space: nowrap;
				bottom: 0px;
				text-overflow: ellipsis;
				overflow: hidden;
				max-width: 50vw;
			}

			&:before {
				content: '';
				position: absolute;
				width: 10px;
				height: 10px;
				top: 0px;
				left: 0px;
				background: #ffc511;
				transform: translateX(-50%) translateY(-50%);
				border-radius: 30px;
			}

			&.resolved {
				border-left: 1px solid rgb(121 134 136);

				> .message {
					background: rgb(121 134 136);
				}

				&:before {
					background: rgb(121 134 136);
				}
			}

			
		}

		.comments-panel {
			padding: 6px 6px 0px 6px;
		}

		.comment-badge {
			padding: 2px 6px;
			background: rgba(255,255,255,0.03);
			color: #999;
			font-size: 9px !important;
			border-radius: 2px;
			cursor: pointer;
			transition: 0.3s;
			margin-bottom: 2px;

			&:hover, &.active {
				background: rgba(255,255,255,0.2);
				color: #ccc;
			}
		}

		.approvers-list {
			color: #fff;

			.approver {
				background: #ffffff8a;
				padding: 4px 8px;
				color: #333;
				border-radius: 2px;
				font-size: 11px;
				margin: 2px;

				> .text {
					overflow: hidden;
					text-overflow: ellipsis;
					max-width: 140px;
				}

				&.approved {
					background: #6eb56e;
					color: #fff;
				}

				&.declined {
					background: #d90000;
					color: #fff;
				}
			}
		}
	}
</style>
